import api from "./api";

/* ============================================================
 * Asset module API client.
 * Mirrors the expense api: a thin wrapper over the shared `api`
 * axios instance (auth token attached by interceptor).
 * Backend router prefix: /assets
 * ========================================================== */

export type AssetStatus = "AVAILABLE" | "IN_USE" | "UNDER_REPAIR" | "RETIRED";
export type AssetType = "OFFICE" | "PERSONAL";
export type AssetOwnership = "COMPANY" | "EMPLOYEE";
export type AssetUsedFor = "WORK" | "WFH";
export type AssetScope = "my" | "all";

export type AssetRequestKind = "NEW" | "TRANSFER" | "RETURN" | "ISSUE";
export type AssetRequestStatus = "PENDING" | "APPROVED" | "REJECTED" | "WITHDRAWN";
export type AssetRequestScope = "mine" | "pending" | "all";
// Repair (ISSUE) requests: THIRD_PARTY = employee arranges + quotes the cost;
// COMPANY = company arranges, no cost from the requester.
export type AssetRepairMode = "THIRD_PARTY" | "COMPANY";

export interface AssetMovement {
  id: string;
  asset_id: string;
  action: string; // CREATED | ASSIGNED | TRANSFERRED | RETURNED | REPAIR | REPAIRED | RETIRED
  from_empt_id: string | null;
  from_name: string | null;
  to_empt_id: string | null;
  to_name: string | null;
  note: string | null;
  moved_on: string | null;
  asset_name?: string | null;
  asset_category?: string | null;
  asset_image_url?: string | null;
}

// One holder of an asset. An asset can be shared by several employees at once,
// and each accepts/rejects their own hand-off independently.
export interface AssetAssignee {
  id: string;
  empt_id: string | null;
  user_id: string | null;
  name: string | null;
  employeeID: string | null;
  assigned_on: string | null;
  assignment_status: "PENDING" | "ACCEPTED" | null;
  note: string | null;
}

export interface Asset {
  id: string;
  name: string;
  category: string | null;
  asset_code: string | null;
  serial_no: string | null;
  description: string | null;
  image_url: string | null;
  asset_type: AssetType;
  ownership: AssetOwnership;
  used_for: AssetUsedFor;
  location: string | null;
  purchase_date: string | null;
  purchase_price: number | null;
  vendor_name: string | null;
  warranty_until: string | null;
  status: AssetStatus;
  // Everyone currently holding the asset. This is the source of truth — the
  // `assigned_*` fields below mirror only the PRIMARY (first) holder and are
  // kept for older call-sites.
  assignees: AssetAssignee[];
  assignee_count: number;
  assigned_empt_id: string | null;
  assigned_user_id: string | null;
  assigned_name: string | null;
  assigned_employeeID: string | null;
  assigned_on: string | null;
  // PENDING until the assignee accepts; ACCEPTED after; null when unassigned.
  assignment_status: "PENDING" | "ACCEPTED" | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  movements?: AssetMovement[]; // present on GET /assets/{id}
}

export interface AssetSummary {
  assigned_to_me: number;
  office_assets: number;
  personal_assets: number;
  wfh_assets: number;
}

export interface AssetRequest {
  id: string;
  kind: AssetRequestKind;
  asset_id: string | null;
  category: string | null;
  title: string | null;
  details: string | null;
  transfer_to_empt_id: string | null;
  transfer_to_name: string | null;
  repair_mode: AssetRepairMode | null;
  repair_cost: number | null;
  /** COMPANY repairs only — which vendor the company sends it to. */
  repair_vendor: string | null;
  employeeID: string | null;
  name: string | null;
  status: AssetRequestStatus;
  submitted_on: string | null;
  withdrawn_on: string | null;
  decision_by: string | null;
  decision_by_name: string | null;
  decision_on: string | null;
  decision_note: string | null;
  emptID: string | null;
  user_id: string | null;
  asset_name?: string | null;
  asset_category?: string | null;
}

export interface AssetCreatePayload {
  name: string;
  category?: string | null;
  asset_code?: string | null;
  serial_no?: string | null;
  description?: string | null;
  image_url?: string | null;
  asset_type?: AssetType;
  ownership?: AssetOwnership | null;
  used_for?: AssetUsedFor;
  location?: string | null;
  purchase_date?: string | null;
  purchase_price?: number | null;
  vendor_name?: string | null;
  warranty_until?: string | null;
  notes?: string | null;
  // Optionally hand the new asset straight to one or more employees.
  assign_to_empt_ids?: string[] | null;
}

export type AssetUpdatePayload = Partial<AssetCreatePayload> & {
  status?: AssetStatus;
};

export interface AssetRequestPayload {
  kind: AssetRequestKind;
  asset_id?: string | null;
  category?: string | null;
  title?: string | null;
  details?: string | null;
  transfer_to_empt_id?: string | null;
  repair_mode?: AssetRepairMode | null;
  repair_cost?: number | null;
  repair_vendor?: string | null;
}

// ---------------------------------------------------------------- assets ----

export const getAssets = async (params: {
  scope?: AssetScope;
  status?: AssetStatus | "";
  asset_type?: AssetType | "";
  q?: string;
} = {}): Promise<{ assets: Asset[]; summary: AssetSummary }> => {
  const res = await api.get("/assets", {
    params: {
      scope: params.scope || "my",
      ...(params.status ? { status: params.status } : {}),
      ...(params.asset_type ? { asset_type: params.asset_type } : {}),
      ...(params.q ? { q: params.q } : {}),
    },
  });
  return res.data;
};

export const getAsset = async (assetId: string): Promise<Asset> => {
  const res = await api.get(`/assets/${assetId}`);
  return res.data.asset;
};

export const createAsset = async (payload: AssetCreatePayload): Promise<Asset> => {
  const res = await api.post("/assets", payload);
  return res.data.asset;
};

export const updateAsset = async (
  assetId: string,
  payload: AssetUpdatePayload
): Promise<Asset> => {
  const res = await api.put(`/assets/${assetId}`, payload);
  return res.data.asset;
};

export const deleteAsset = async (assetId: string): Promise<void> => {
  await api.delete(`/assets/${assetId}`);
};

/** Set who holds an asset.
 *
 * REPLACE semantics: `empt_ids` is the FULL desired holder list, not a delta.
 * Employees added get notified; holders left out are released back to stock.
 * Passing [] returns the asset to stock. Safe to include employees who already
 * hold it — they're left untouched rather than re-notified.
 */
export const assignAsset = async (
  assetId: string,
  payload: {
    empt_ids: string[];
    note?: string;
    used_for?: AssetUsedFor;
    location?: string;
  }
): Promise<Asset> => {
  const res = await api.post(`/assets/${assetId}/assign`, payload);
  return res.data.asset;
};

/** Return an asset to stock. `emptId` narrows it to one holder of a shared
 * asset; omitted, a manager's call releases every holder. */
export const returnAsset = async (
  assetId: string,
  note?: string,
  emptId?: string
): Promise<Asset> => {
  const res = await api.post(`/assets/${assetId}/return`, {
    note: note || null,
    ...(emptId ? { empt_id: emptId } : {}),
  });
  return res.data.asset;
};

// Assignee sign-off on a pending assignment.
export const acceptAsset = async (assetId: string): Promise<Asset> => {
  const res = await api.post(`/assets/${assetId}/accept`);
  return res.data.asset;
};

export const rejectAsset = async (assetId: string, reason: string): Promise<Asset> => {
  const res = await api.post(`/assets/${assetId}/reject`, { note: reason });
  return res.data.asset;
};

// ------------------------------------------------------------ categories ----

export const getAssetCategories = async (): Promise<string[]> => {
  const res = await api.get("/assets/categories");
  return res.data.categories || [];
};

export const addAssetCategory = async (name: string): Promise<void> => {
  await api.post("/assets/categories", { name });
};

// ------------------------------------------------------------- movements ----

export const getRecentMovements = async (limit = 20): Promise<AssetMovement[]> => {
  const res = await api.get("/assets/movements/recent", { params: { limit } });
  return res.data.movements || [];
};

// -------------------------------------------------------------- requests ----

export const getAssetRequests = async (
  scope: AssetRequestScope = "mine",
  status?: AssetRequestStatus
): Promise<AssetRequest[]> => {
  const res = await api.get("/assets/requests", {
    params: { scope, ...(status ? { status } : {}) },
  });
  return res.data.requests || [];
};

export const createAssetRequest = async (
  payload: AssetRequestPayload
): Promise<AssetRequest> => {
  const res = await api.post("/assets/requests", payload);
  return res.data.request;
};

export const withdrawAssetRequest = async (requestId: string): Promise<AssetRequest> => {
  const res = await api.post(`/assets/requests/${requestId}/withdraw`);
  return res.data.request;
};

export const decideAssetRequest = async (
  requestId: string,
  status: "APPROVED" | "REJECTED",
  note?: string
): Promise<{ request: AssetRequest; expense_created: boolean; message: string }> => {
  const res = await api.post(
    `/assets/requests/${requestId}/decision`,
    { note: note || null },
    { params: { status } }
  );
  return {
    request: res.data.request,
    expense_created: !!res.data.expense_created,
    message: res.data.message || "",
  };
};

// ---------------------------------------------------------- notifications ----
// Asset assign/accept alerts ride the shared notification bell via a namespaced
// task_id "asset:<asset_id>". The bell hydrates the unread feed and marks rows
// read on dismissal (mirrors the expense/salary notification feeds).

export const getAssetNotifications = (limit = 50) =>
  api.get(`/assets/notifications/unread?limit=${limit}`);

export const markAssetNotificationRead = (id: string) =>
  api.post(`/assets/notifications/${id}/read`);
